import {storeToRefs} from 'pinia';
import {isDoctorWidgetContext, isWidgetContext} from '~/stores/auth';
import {apiRouteGroup} from '~/utils/apiErrorTelemetry';

type ApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

function toApiError(
    payload: unknown,
    fallbackMessage: string,
    statusCode?: number,
    attribution?: {method: ApiMethod; routeGroup: string},
    retryAfterHeader?: string | null,
): Error & Record<string, unknown> {
    const details = payload && typeof payload === 'object' && !Array.isArray(payload)
        ? payload as Record<string, unknown>
        : {};
    const message = typeof details.message === 'string' && details.message.trim()
        ? details.message
        : fallbackMessage;
    const apiError = new Error(message) as Error & Record<string, unknown>;
    apiError.name = 'ApiError';

    for (const [key, value] of Object.entries(details)) {
        if (!['message', 'name', 'stack', 'cause', 'apiMethod', 'apiRouteGroup', '__proto__', 'constructor', 'prototype', 'toString'].includes(key)) {
            apiError[key] = value;
        }
    }
    if (typeof statusCode === 'number') {
        apiError.statusCode = statusCode;
    }
    if (statusCode === 429) {
        const retryAfter = typeof details.retryAfter === 'number'
            ? details.retryAfter
            : typeof details.retryAfterSeconds === 'number'
                ? details.retryAfterSeconds
                : Number(retryAfterHeader);

        if (Number.isFinite(retryAfter) && retryAfter > 0) {
            apiError.retryAfter = retryAfter;
            if (typeof details.retryAfterSeconds === 'number') {
                apiError.retryAfterSeconds = details.retryAfterSeconds;
            }
            if (!/(?:секунд|минут|час)/iu.test(message)) {
                const separator = /[.!?…]\s*$/u.test(message) ? ' ' : '. ';
                apiError.message = `${message}${separator}Повторите через ${formatRetryDuration(retryAfter)}.`;
            }
        }
    }
    if (attribution) {
        apiError.apiMethod = attribution.method;
        apiError.apiRouteGroup = attribution.routeGroup;
    }

    return apiError;
}

function formatRetryDuration(seconds: number): string {
    if (seconds >= 3600) {
        const hours = Math.ceil(seconds / 3600);
        return `${hours} ${pluralize(hours, 'час', 'часа', 'часов')}`;
    }

    if (seconds < 60) {
        return `${Math.ceil(seconds)} ${pluralize(Math.ceil(seconds), 'секунду', 'секунды', 'секунд')}`;
    }

    const minutes = Math.ceil(seconds / 60);
    return `${minutes} ${pluralize(minutes, 'минуту', 'минуты', 'минут')}`;
}

function pluralize(value: number, one: string, few: string, many: string): string {
    const lastTwo = value % 100;
    if (lastTwo >= 11 && lastTwo <= 14) {
        return many;
    }

    switch (value % 10) {
        case 1:
            return one;
        case 2:
        case 3:
        case 4:
            return few;
        default:
            return many;
    }
}

export function useApi() {
    const baseURL: string = useApiBaseUrl();
    const authStore = useAuthStore();
    const {accessToken} = storeToRefs(authStore);
    const requestCookie = useRequestHeaders(['cookie']).cookie;
    const currentFrontendPage = () => {
        if (!import.meta.client) {
            return undefined;
        }

        return window.location.href;
    };

    // Источник записи в админке («С виджета»): бэкенд пишет его в meta.source при
    // создании брони и заявки. Заголовок идёт на всех запросах, а не только на
    // создающих: значение «site» отличает записи нашего фронта от записей без
    // источника — по ним виджет приходится выводить из UTM, а он живёт в
    // localStorage бессрочно и врёт после захода по виджетной ссылке.
    const requestSource = () => {
        if (!import.meta.client) {
            return undefined;
        }

        if (isDoctorWidgetContext()) {
            return 'doctor_widget';
        }

        return isWidgetContext() ? 'widget' : 'site';
    };

    const headers = computed(() => {
        return {
            "Accept": "application/json",
            ...(currentFrontendPage() ? {
                "X-Frontend-Page": currentFrontendPage()
            } : {}),
            ...(requestSource() ? {
                "X-Vetsy-Source": requestSource()
            } : {}),
            ...(accessToken?.value ? {
                "AuthToken": accessToken.value
            } : {})
        }
    });

    // Helper function to add XDEBUG query parameters in development
    const addXdebugParams = (options?: {[key: string]: any}) => {
        if (import.meta.dev) {
            return {
                ...options,
                query: {
                    ...(options?.query || {}),
                    XDEBUG_SESSION_START: 'PHPSTORM'
                }
            };
        }
        return options || {};
    };

    // SSR Nuxt не отправляет cookie браузера на внутренний API сам.
    // Без этого кабинет, открытый с витрины, видит сессию и тут же получает 401 на заказах.
    const requestHeaders = () => {
        const result: Record<string, string> = {...headers.value}
        if (requestCookie) {
            result.Cookie = requestCookie
        }
        return result
    }

    const apiQuery = <Item>(requestUrl: string, method: ApiMethod = 'POST', options?: {[key: string]: any}): Promise<Item> => {
        const attribution = {method, routeGroup: apiRouteGroup(requestUrl)};
        return new Promise((resolve, reject) => {
            $fetch(requestUrl, {
                    baseURL,
                    method,
                    headers: requestHeaders(),
                    retry: method === 'GET' ? 1 : 0,
                    retryDelay: 500,
                    timeout: 90000,
                    credentials: 'include',
                    ...addXdebugParams(options)
                })
                .then(async (data) => {
                    resolve(data as Item)
                }).catch(async (error) => {
                    console.error('[api]', {
                        method,
                        routeGroup: attribution.routeGroup,
                        statusCode: error?.response?.status ?? error?.statusCode ?? null,
                    });
                    if (error.response) {
                        reject(toApiError(
                            error.response._data,
                            'Неизвестная ошибка',
                            error.response.status,
                            attribution,
                            error.response.headers?.get?.('Retry-After'),
                        ))
                    } else if (error.data) {
                        reject(toApiError(error.data, 'Неизвестная ошибка', error.statusCode ?? error.status, attribution))
                    } else {
                        const message = String(error?.message ?? '');
                        const isNetworkBlock = message.toLowerCase().includes('failed to fetch')
                            || message.toLowerCase().includes('networkerror')
                            || message.toLowerCase().includes('load failed');

                        reject(toApiError(null, isNetworkBlock
                            ? (import.meta.dev
                                ? 'Нет связи с API. Откройте сайт через http://vetsy.test, не localhost.'
                                : 'Нет связи с сервером. Попробуйте позже.')
                            : (message || 'Неизвестная ошибка'), undefined, attribution));
                    }
                });
        });
    }

    const apiDownload = (requestUrl: string, method: 'GET' | 'POST' | 'PUT' = 'POST', options?: {[key: string]: any}) => {
        const attribution = {method, routeGroup: apiRouteGroup(requestUrl)};
        return new Promise(async (resolve, reject) => {
            $fetch<Blob>(requestUrl, {
                baseURL,
                method,
                headers: headers.value,
                retry: method === 'GET' ? 1 : 0,
                retryDelay: 500,
                timeout: 90000,
                credentials: 'include',
                ...addXdebugParams(options)
            })
                .then(async (res: Blob) => {
                    if (res) {
                        if (res instanceof Blob) {
                            let file = window.URL.createObjectURL(res);
                            let docUrl = document.createElement('a');
                            docUrl.href = file;
                            let date = new Date();
                            let postfix = (date.toLocaleDateString() + " " + date.toLocaleTimeString());
                            postfix = postfix.replace(/\D+/g, "_");
                            docUrl.setAttribute('download', requestUrl);
                            document.body.appendChild(docUrl);
                            docUrl.click();
                            resolve(true)
                        } else {
                            reject(toApiError(null, 'Ошибка загрузки файла', undefined, attribution))
                        }
                    } else {
                        reject(toApiError(null, 'Файл не найден', undefined, attribution))
                    }
                }).catch(async (error) => {
                    if (error.response) {
                        reject(toApiError(error.response._data, 'Ошибка загрузки файла', error.response.status, attribution))
                    } else {
                        reject(toApiError(null, 'Неизвестная ошибка', undefined, attribution))
                    }
                });
        });
    }

    const apiPrivateDownload = (fileId: number | string, method: 'GET' | 'POST' | 'PUT' = 'POST', options?: {[key: string]: any}) => {
        let requestUrl = typeof fileId === 'string' ? fileId : (baseURL + `/media/private/${fileId}/view`);
        const attribution = {method, routeGroup: apiRouteGroup(requestUrl)};
        
        // Add XDEBUG parameter in development mode (baseURL may be relative — avoid new URL()).
        if (import.meta.dev) {
            const separator = requestUrl.includes('?') ? '&' : '?';
            requestUrl += `${separator}XDEBUG_SESSION_START=PHPSTORM`;
        }
        
        return new Promise(async (resolve, reject) => {
            fetch(requestUrl, {
                method,
                headers: headers.value,
                credentials: 'include',
                ...options
            })
                .then(async (res) => {
                    if (res) {
                        // Статус проверяем до того, как строить файл: у ответа с ошибкой
                        // тело — JSON, но res.blob() отдаёт Blob и для него, поэтому
                        // проверка `blob instanceof Blob` пропускала ошибку как файл —
                        // владелец «скачивал» текст ошибки и не видел ни одного уведомления.
                        if (!res.ok) {
                            reject(toApiError(await readDownloadError(res), 'Ошибка загрузки файла', res.status, attribution));
                            return;
                        }

                        const contentDisposition = res.headers.get('content-disposition');
                        let fileName = '';
                        if (contentDisposition) {
                            fileName = getFileName(contentDisposition);
                        }
                        const blob = await res.blob();
                        if (blob instanceof Blob) {
                            let file = window.URL.createObjectURL(blob);
                            let docUrl = document.createElement('a');
                            docUrl.href = file;
                            let date = new Date();
                            let postfix = (date.toLocaleDateString() + " " + date.toLocaleTimeString());
                            postfix = postfix.replace(/\D+/g, "_");
                            docUrl.setAttribute('download', fileName || requestUrl);
                            document.body.appendChild(docUrl);
                            docUrl.click();
                            resolve(true)
                        } else {
                            reject(toApiError(null, 'Ошибка загрузки файла', undefined, attribution));
                        }
                    } else {
                        reject(toApiError(null, 'Файл не найден', undefined, attribution));
                    }
                }).catch(async (error) => {
                    if (error.response) {
                        reject(toApiError(error.response._data, 'Ошибка загрузки файла', error.response.status, attribution))
                    } else {
                        reject(toApiError(null, 'Неизвестная ошибка', undefined, attribution))
                    }
                });
        });
    }

    /**
     * Тело неуспешного ответа приватной загрузки: бэкенд отдаёт `{message}`, и он же
     * попадает в тост. Нечитаемое тело не должно подменять причину — тогда общий текст.
     */
    const readDownloadError = async (res: Response): Promise<{[key: string]: any}> => {
        try {
            const payload = await res.json();
            if (payload && typeof payload === 'object') {
                return payload;
            }
        } catch {
            // Не JSON (html-страница ошибки, пустое тело) — ниже общий текст.
        }

        return {message: 'Ошибка загрузки файла'};
    };

    // Разбор заголовка — в utils/contentDisposition.ts: это чистая логика, к тому же
    // с краевыми случаями RFC 5987, которые проще держать под юнит-тестами.
    const getFileName = (contentDisposition: string): string =>
        parseContentDispositionFileName(contentDisposition);

    const privateFileHeaders = computed(() => ({
        ...headers.value,
        Accept: '*/*',
    }));

    type TPrivateBlobFetchOptions = {
        mimeTypeHint?: string | null;
    };

    const fetchPrivateBlob = async (
        url: string,
        options?: TPrivateBlobFetchOptions,
    ): Promise<Blob | null> => {
        try {
            const res = await fetch(url, {
                method: 'GET',
                headers: privateFileHeaders.value,
                credentials: 'include',
            });

            if (!res.ok) {
                return null;
            }

            let blob = await res.blob();
            const headerType = res.headers.get('content-type')?.split(';')[0]?.trim() || '';
            const effectiveType = blob.type && blob.type !== 'application/octet-stream'
                ? blob.type
                : (options?.mimeTypeHint || headerType || blob.type);

            if (effectiveType && effectiveType !== blob.type) {
                blob = new Blob([blob], { type: effectiveType });
            }

            return blob;
        } catch {
            return null;
        }
    };

    const apiPrivateBlobUrl = async (
        url: string,
        options?: TPrivateBlobFetchOptions,
    ): Promise<string | null> => {
        const blob = await fetchPrivateBlob(url, options);
        return blob ? URL.createObjectURL(blob) : null;
    };

    const apiPrivateImagePreviewUrl = async (
        url: string,
        options?: TPrivateBlobFetchOptions,
    ): Promise<string | null> => {
        const blob = await fetchPrivateBlob(url, options);
        if (!blob) {
            return null;
        }

        const effectiveType = blob.type || options?.mimeTypeHint || '';
        if (!isImageMimeType(effectiveType)) {
            return null;
        }

        return URL.createObjectURL(blob);
    };

    return {apiQuery, apiDownload, apiPrivateDownload, apiPrivateBlobUrl, apiPrivateImagePreviewUrl};
}
